import { create, all } from 'mathjs';
import { useState } from 'react';

type Operator = '+' | '-' | 'x' | '/' | '%' | '^x' | 'Sqr';

const math = create(all, {
  number: 'BigNumber',
  precision: 64,
});

const NUMBER_BUTTONS = [7, 8, 9, 4, 5, 6, 1, 2, 3, 0];
const OPERATOR_BUTTONS: { label: string; op: Operator | 'AC' }[] = [
  { label: '/', op: '/' },
  { label: '%', op: '%' },
  { label: 'x', op: 'x' },
  { label: 'AC', op: 'AC' },
  { label: '-', op: '-' },
  { label: 'xʸ', op: '^x' },
  { label: '+', op: '+' },
  { label: '√', op: 'Sqr' },
];

export default function Calculator() {
  const [display, setDisplay] = useState('0');
  const [showExpression, setShowExpression] = useState('');
  const [fullExpression, setFullExpression] = useState('');
  const [waitingForOperand, setWaitingForOperand] = useState(false);

  const clearDisplay = () => {
    setDisplay('0');
    setShowExpression('');
    setFullExpression('');
    setWaitingForOperand(false);
  };

  const handleNumberClick = (number: string) => {
    if (showExpression.includes('=')) {
      setWaitingForOperand(false);
      setDisplay(number);
      setShowExpression('');
      setFullExpression(number);
      return;
    }

    const newDisplay = waitingForOperand ? number : display === '0' ? number : display + number;
    setDisplay(newDisplay);
    setWaitingForOperand(false);
    setFullExpression(fullExpression + number);
    setShowExpression(fullExpression + number);
  };

  const handleDecimalPoint = () => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
      setFullExpression(fullExpression + '0.');
    } else if (!display.includes('.')) {
      setDisplay(display + '.');
      setFullExpression(fullExpression + '.');
    }
  };

  const handleOperatorClick = (op: Operator) => {
    const currentValue = math.bignumber(display);

    if (op === 'Sqr') {
      const sqrtExpr = `sqrt(${currentValue})`;
      setShowExpression(`${sqrtExpr}`);
      setFullExpression(sqrtExpr);
      setWaitingForOperand(true);
      return;
    }

    if (op === '^x') {
      const updated = `${fullExpression}^`;
      setFullExpression(updated);
      setShowExpression(updated);
      setWaitingForOperand(true);
      return;
    }

    if (op === '%') {
      const result = currentValue.div(100);
      setDisplay(result.toString());
      const percentExpr = `(${currentValue})/100`;
      setShowExpression(`${currentValue}% = ${result}`);
      setFullExpression(percentExpr);
      setWaitingForOperand(false);
      return;
    }

    const operatorSymbol = op === 'x' ? '*' : op;
    const trimmed = fullExpression.trim();
    const lastChar = trimmed[trimmed.length - 1];
    const isLastCharOperator = ['+', '-', '*', '/', '^'].includes(lastChar);

    const updated =
      trimmed === ''
        ? ''
        : isLastCharOperator
          ? trimmed.slice(0, -1) + operatorSymbol
          : `${trimmed} ${operatorSymbol} `;

    setFullExpression(updated);
    setShowExpression(updated);
    setWaitingForOperand(true);
  };

  const calculateResult = () => {
    try {
      const result = math.evaluate(fullExpression);
      const roundedResult = math.round(result, 6);

      setDisplay(roundedResult.toString());
      setShowExpression(fullExpression);
      setFullExpression(roundedResult.toString());
      setWaitingForOperand(false);
    } catch {
      setDisplay('');
      setShowExpression('');
      setFullExpression('');
      setWaitingForOperand(false);
    }
  };

  return (
    <div className='mx-auto max-w-xs rounded-lg bg-gray-100 p-4'>
      <div className='border-grey-300 mb-4 min-h-[50px] rounded-lg border bg-white px-6 py-1 text-right text-2xl break-words whitespace-pre-wrap'>
        <div className='mb-1 text-sm text-gray-500'>{showExpression}</div>
        <span className='font-semibold break-words'>{display}</span>
      </div>

      <div className='flex justify-between gap-5'>
        {/* Numbers */}
        <div className='grid grid-cols-3 gap-2'>
          {NUMBER_BUTTONS.map((num) => (
            <button
              key={num}
              onClick={() => handleNumberClick(num.toString())}
              className='border-grey-300 hover:bg-grey-50 size-12 cursor-pointer rounded-lg border bg-white p-2.5 text-black transition'
            >
              {num}
            </button>
          ))}
          <button
            onClick={handleDecimalPoint}
            className='border-grey-300 hover:bg-grey-50 size-12 cursor-pointer rounded-lg border bg-white p-2.5 text-black transition'
          >
            .
          </button>
          <button
            onClick={calculateResult}
            className='bg-primary hover:bg-primary-700 size-12 cursor-pointer rounded-lg p-2.5 text-black transition'
          >
            =
          </button>
        </div>

        {/* Operators */}
        <div className='grid grid-cols-2 gap-2'>
          {OPERATOR_BUTTONS.map(({ label, op }) => (
            <button
              key={label}
              onClick={() => {
                if (op === 'AC') clearDisplay();
                else handleOperatorClick(op as Operator);
              }}
              className='border-primary hover:bg-primary-100 size-12 cursor-pointer rounded-lg border bg-white p-2.5 text-black transition'
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
